<template>
  <div
    class="modal-overlay"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    @click.self="$emit('cancel')"
  >
    <div class="modal">
      <h2 :id="titleId" class="modal-title">{{ titleText }}</h2>

      <p
        v-if="serverError && !readOnly"
        class="server-error"
        role="alert"
      >{{ serverError }}</p>

      <div class="modal-body">
        <div class="field">
          <label class="field-label" :for="`${uid}-slug`">Slug</label>
          <input
            :id="`${uid}-slug`"
            type="text"
            class="field-input mono"
            :value="section.slug"
            disabled
          >
          <p class="field-hint dim">Slug cannot be changed.</p>
        </div>

        <div class="field">
          <label class="field-label">Name</label>
          <div
            v-for="lang in LANGS"
            :key="lang.code"
            class="lang-row"
          >
            <span class="lang-code mono">{{ lang.code.toUpperCase() }}</span>
            <input
              :id="`${uid}-name-${lang.code}`"
              v-model="form[lang.code]"
              type="text"
              class="field-input lang-input"
              :class="{ 'has-error': lang.code === 'de' && errors.de && touched.de }"
              :placeholder="lang.placeholder"
              :disabled="readOnly || submitting"
              :aria-label="`Name in ${lang.label}`"
              @blur="lang.code === 'de' ? (touched.de = true) : null"
            >
          </div>
          <p
            v-if="errors.de && touched.de && !readOnly"
            class="field-error"
          >{{ errors.de }}</p>
          <p v-if="!readOnly" class="field-hint">
            At least the German name is required. Other languages can be
            filled in later or via the translation upload.
          </p>
        </div>
      </div>

      <div class="modal-footer">
        <template v-if="readOnly">
          <span></span>
          <button
            type="button"
            class="btn btn-secondary"
            @click="$emit('cancel')"
          >Close</button>
        </template>
        <template v-else>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="submitting"
            @click="$emit('cancel')"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isValid || submitting"
            @click="onSubmit"
          >{{ submitting ? 'Saving…' : 'Save changes' }}</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
let uidCounter = 0;

const LANGS = [
  { code: 'de', label: 'Deutsch', placeholder: 'z. B. Gemüse' },
  { code: 'en', label: 'English', placeholder: 'e.g. Vegetables' },
  { code: 'fr', label: 'Français', placeholder: 'p. ex. Légumes' },
  { code: 'it', label: 'Italiano', placeholder: 'es. Verdure' },
];

export default {
  name: 'SectionEditorModal',
  props: {
    section: { type: Object, required: true },
    mode: {
      type: String,
      default: 'edit',
      validator: v => ['edit', 'view'].includes(v),
    },
    // True while the parent's PATCH is in flight. Disables inputs +
    // Cancel + Save and swaps the Save label to "Saving…". Server
    // errors land in serverError.
    submitting: { type: Boolean, default: false },
    serverError: { type: String, default: null },
  },
  emits: ['submit', 'cancel'],
  data() {
    uidCounter += 1;
    const name = (this.section && typeof this.section.name === 'object') ? this.section.name : {};
    return {
      uid: `sem-${uidCounter}`,
      LANGS,
      form: {
        de: typeof name.de === 'string' ? name.de : '',
        en: typeof name.en === 'string' ? name.en : '',
        fr: typeof name.fr === 'string' ? name.fr : '',
        it: typeof name.it === 'string' ? name.it : '',
      },
      touched: { de: false },
    };
  },
  computed: {
    titleId() {
      return `${this.uid}-title`;
    },
    readOnly() {
      return this.mode === 'view';
    },
    titleText() {
      return this.readOnly ? 'View section' : 'Edit section';
    },
    errors() {
      const e = {};
      if (!this.readOnly) {
        const de = (this.form.de || '').trim();
        if (!de) e.de = 'German name is required.';
      }
      return e;
    },
    isValid() {
      return Object.keys(this.errors).length === 0;
    },
  },
  methods: {
    onSubmit() {
      this.touched.de = true;
      if (!this.isValid) return;
      // Build the new JSONB. Whitespace-only inputs become null so the
      // backend doesn't store empty strings disguised as filled
      // translations.
      const name = {};
      for (const lang of LANGS) {
        const v = (this.form[lang.code] || '').trim();
        name[lang.code] = v.length > 0 ? v : null;
      }
      this.$emit('submit', { name });
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  --color-bg-card: #ffffff;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
  font-family: var(--font-sans);
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 1rem;
  color: var(--color-text-primary);
}

.server-error {
  font-size: 13px;
  color: #6B1F18;
  background: rgba(200, 57, 46, 0.10);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1rem;
  line-height: 1.5;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  margin-bottom: 1.25rem;
  padding-bottom: 4px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.field-input {
  width: 100%;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.4;
}

.field-input.mono {
  font-family: var(--font-mono);
  font-size: 13px;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.field-input:disabled {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
  cursor: not-allowed;
}

.field-input.has-error {
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.field-hint {
  font-size: 11px;
  color: var(--color-text-tertiary);
  margin: 0;
  line-height: 1.4;
}

.field-hint.dim {
  font-style: italic;
}

.field-error {
  font-size: 12px;
  color: var(--color-bad);
  margin: 0;
}

/* One row per language: small monospace code badge on the left, the
   input fills the rest. Same visual treatment for all four languages —
   the validation rule is what distinguishes German, not the styling. */
.lang-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lang-code {
  flex-shrink: 0;
  width: 28px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  padding: 4px 6px;
  background: var(--color-bg-soft);
  border-radius: 4px;
  border: 0.5px solid var(--color-border);
}

.lang-input {
  flex: 1;
  min-width: 0;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding-top: 0.5rem;
}

.btn {
  padding: 10px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}
</style>
